// src/features/Analytics/mlResourcePredictor.js
import { MLDataPreprocessor } from '../../utils/mlDataPreprocessor';

export class MLResourcePredictor {
  constructor() {
    this.trainingData = [];
    this.waterModel = null;
    this.propaneModel = null;
    this.electricityModel = null;
  }

  // Додаємо дані для тренування моделей ресурсів
  addTrainingData(records) {
    const processed = records
      .map(record => MLDataPreprocessor.normalizeRecord(record))
      .filter(record => record !== null);
    
    this.trainingData.push(...processed);
  }

  // Тренуємо модель для води
  trainWaterModel() {
    const features = [];
    const targets = [];

    this.trainingData.forEach(record => {
      if (record && record.resourceFeatures && record.resourceFeatures.waterConsumption > 0) {
        features.push([
          record.productionFeatures.totalProduction,
          record.productionFeatures.totalCycles,
          record.timeFeatures.dayOfWeek,
          record.timeFeatures.month,
          record.shiftFeatures.operatorsCount
        ]);
        targets.push(record.resourceFeatures.waterConsumption);
      }
    });

    if (features.length < 3) return null;
    
    this.waterModel = this.trainConsumptionModel(features, targets, 'water');
    return this.waterModel;
  }

  // Тренуємо модель для пропану
  trainPropaneModel() {
    const features = [];
    const targets = [];

    this.trainingData.forEach(record => {
      if (record && record.resourceFeatures && record.resourceFeatures.propaneConsumption > 0) {
        features.push([
          record.productionFeatures.totalProduction,
          record.timeFeatures.month,
          record.timeFeatures.dayOfWeek,
          record.productionFeatures.efficiency
        ]);
        targets.push(record.resourceFeatures.propaneConsumption);
      }
    });

    if (features.length < 3) return null;
    
    this.propaneModel = this.trainConsumptionModel(features, targets, 'propane');
    return this.propaneModel;
  }

  // Загальна модель споживання
  trainConsumptionModel(features, targets, resourceType) {
    // Коефіцієнти для різних типів ресурсів
    const resourceCoefficients = {
      water: [0.8, 50, 100, 20, 10],
      propane: [0.3, 5, 2, 0.5],
      electricity: [0.2, 10, 5, 1]
    };

    const coefficients = resourceCoefficients[resourceType] || [1, 1, 1, 1, 1];
    
    return {
      coefficients,
      intercept: this.calculateIntercept(targets),
      resourceType,
      featuresCount: features[0]?.length || 0,
      trainedAt: new Date()
    };
  }

  // Прогнозуємо споживання на наступні дні
  predictConsumption(productionPredictions, days = 7) {
    if (!this.waterModel && !this.propaneModel) {
      return this.fallbackConsumptionPrediction(productionPredictions.length || days);
    }

    return productionPredictions.map(prodPrediction => {
      const date = new Date(prodPrediction.date);
      const dayOfWeek = date.getDay();
      const month = date.getMonth();
      
      return {
        date: prodPrediction.date,
        predictedProduction: prodPrediction.predictedProduction,
        water: this.predictWaterConsumption(prodPrediction.predictedProduction, dayOfWeek, month),
        propane: this.predictPropaneConsumption(prodPrediction.predictedProduction, month),
        electricity: this.predictElectricityConsumption(prodPrediction.predictedProduction),
        confidence: (prodPrediction.confidence || 50) * 0.8
      };
    });
  }

  // Прогнозування споживання води
  predictWaterConsumption(production, dayOfWeek, month) {
    if (!this.waterModel) {
      return Math.round(production * 5 + 2000);
    }

    const features = [production, 0, dayOfWeek, month, 2];
    return this.predictWithModel(features, this.waterModel);
  }

  // Прогнозування споживання пропану
  predictPropaneConsumption(production, month) {
    if (!this.propaneModel) {
      const seasonFactor = month >= 10 || month <= 3 ? 1.3 : 0.8;
      return Math.round(production * 0.1 * seasonFactor + 50);
    }

    const features = [production, month, 1, 80];
    return this.predictWithModel(features, this.propaneModel);
  }

  // Прогнозування споживання електрики
  predictElectricityConsumption(production) {
    return Math.round(production * 0.3 + 800);
  }

  // Допоміжні методи
  predictWithModel(features, model) {
    if (!model || !model.coefficients) return 0;
    
    let prediction = model.intercept || 0;
    features.forEach((feature, index) => {
      const coefficient = model.coefficients[index] || 0;
      prediction += coefficient * feature;
    });
    
    return Math.max(0, Math.round(prediction));
  }

  calculateIntercept(targets) {
    if (targets.length === 0) return 0;
    const avg = targets.reduce((sum, target) => sum + target, 0) / targets.length;
    return avg * 0.7;
  }

  // Резервний прогноз
  fallbackConsumptionPrediction(days) {
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() + i + 1);
      
      return {
        date: date.toISOString().split('T')[0],
        predictedProduction: 1500,
        water: 8000,
        propane: 200,
        electricity: 1200,
        confidence: 50,
        method: 'fallback_consumption'
      };
    });
  }

  // Статистика моделей
  getModelStats() {
    return {
      waterModel: {
        isTrained: !!this.waterModel,
        dataPoints: this.trainingData.filter(d => d && d.resourceFeatures && d.resourceFeatures.waterConsumption > 0).length
      },
      propaneModel: {
        isTrained: !!this.propaneModel,
        dataPoints: this.trainingData.filter(d => d && d.resourceFeatures && d.resourceFeatures.propaneConsumption > 0).length
      },
      totalTrainingData: this.trainingData.length
    };
  }
}

export default MLResourcePredictor;


